import type { CSSProperties } from "react";
import { boxCss, type BoxSides } from "@/purpose-sections/length";
import {
  resolveBackground,
  type ResolvedBackground,
} from "@/utils/background-layer";

export const SEARCH_PLACEMENTS = ["none", "media", "above", "below"] as const;
export const SEARCH_POSITIONS = ["top", "center", "bottom"] as const;
export const SEARCH_PLACEHOLDER = "Adresse, by eller postnr.";
export const SEARCH_BUTTON_TEXT = "Søg";

export type SearchPlacement = (typeof SEARCH_PLACEMENTS)[number];
export type SearchPosition = (typeof SEARCH_POSITIONS)[number];
export type SearchAlign = "left" | "center" | "right";

/** Boligsøgning's and Medie og tekst's search bar: the bar itself (SearchBarComponent's look) and the box around it. */
export interface SearchSettings {
  variant?: "standard" | "glass";
  bgColor?: string;
  input?: { placeholderText?: string };
  button?: { text?: string; bgColor?: string; textColor?: string };
  /** a CSS length; absent fills the room it stands in */
  width?: string;
  /** spacing tokens or CSS lengths per side, `mobile | desktop` */
  padding?: string;
  margin?: string;
  /** Medie og tekst only */
  placement?: SearchPlacement;
  position?: SearchPosition;
}

const WIDTH = /^\d+(?:\.\d+)?(?:px|rem|em|vw|%)$/i;
const text = (value: unknown) =>
  typeof value === "string" ? value : undefined;
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const oneOf = <T extends string>(values: readonly T[], value: unknown) =>
  (values as readonly unknown[]).includes(value) ? (value as T) : undefined;
const defined = <T extends Record<string, unknown>>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T;

/** The known keys only, each dropped when unreadable; undefined when nothing is left. */
export function normalizeSearch(raw: unknown): SearchSettings | undefined {
  const value = record(raw);
  const input = record(value.input);
  const button = record(value.button);
  const buttonKeys = defined({
    text: text(button.text),
    bgColor: text(button.bgColor),
    textColor: text(button.textColor),
  });
  const search = defined({
    variant: oneOf(["standard", "glass"] as const, value.variant),
    bgColor: text(value.bgColor),
    input:
      text(input.placeholderText) !== undefined
        ? { placeholderText: text(input.placeholderText) }
        : undefined,
    button: Object.keys(buttonKeys).length ? buttonKeys : undefined,
    width: text(value.width),
    padding: text(value.padding),
    margin: text(value.margin),
    placement: oneOf(SEARCH_PLACEMENTS, value.placement),
    position: oneOf(SEARCH_POSITIONS, value.position),
  });
  return Object.keys(search).length ? search : undefined;
}

/** Medie og tekst: where its search bar stands. On the media only where a picture or video stands beside or above the
 *  text; on a picture behind the text, or without one, it stands under the text and the buttons. */
export function imageTextSearchPlacement(
  search: SearchSettings | undefined,
  imagePlacement: string,
  hasMedia = true,
): Exclude<SearchPlacement, "none"> | undefined {
  const placement = search?.placement;
  if (!placement || placement === "none") return undefined;
  if (placement !== "media") return placement;
  return hasMedia && ["left", "right", "top"].includes(imagePlacement)
    ? "media"
    : "below";
}

const sides = (box?: BoxSides) =>
  box ? `${box.top} ${box.right} ${box.bottom} ${box.left}` : undefined;
const FLEX: Record<SearchAlign | SearchPosition, string> = {
  left: "flex-start",
  top: "flex-start",
  center: "center",
  right: "flex-end",
  bottom: "flex-end",
};

/** The box's width, padding and margin, and where the bar sits in it. `margin` is the default for an absent margin:
 *  on a picture the box keeps 32 px from its edges. */
export function searchBlockStyle(
  search: SearchSettings | undefined,
  {
    align,
    position,
    margin,
  }: { align?: SearchAlign; position?: SearchPosition; margin?: string } = {},
): CSSProperties {
  const width = text(search?.width)?.trim();
  const padding = boxCss(search?.padding);
  const outer = boxCss(search?.margin ?? margin);
  return defined({
    "--re-search-width": width && WIDTH.test(width) ? width : undefined,
    "--re-search-padding": sides(padding.mobile),
    "--re-search-padding-desktop": sides(padding.desktop),
    "--re-search-margin": sides(outer.mobile),
    "--re-search-margin-desktop": sides(outer.desktop),
    "--re-search-justify": align ? FLEX[align] : undefined,
    "--re-search-align": position ? FLEX[position] : undefined,
  }) as CSSProperties;
}

export interface PropertySearchSettings {
  heading?: string;
  text?: string;
  targetPage?: string;
  search?: Omit<SearchSettings, "placement" | "position">;
  imageUrl?: string;
  mediaKind?: "image" | "video";
  videoUrl?: string;
  videoType?: "realequity" | "youtube" | "vimeo";
  video?: {
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    controls?: boolean;
  };
  imageFocus?: { x?: string; y?: string };
  tint?: "none" | "dark" | "light";
  contentPosition?: SearchPosition;
  styles?: { content?: { align?: SearchAlign } };
}

export interface PropertySearchStage {
  /** the picture or video behind the search box with its tint; `none` without one */
  background: ResolvedBackground;
  /** the text colour on the picture: light on a dark or no tint, dark on a light tint */
  ink?: "light" | "dark";
  align: SearchAlign;
  position: SearchPosition;
}

/** Absent keys draw Boligsøgning as before: no picture, the box to the left and as wide as the section. */
export function propertySearchStage(
  c: PropertySearchSettings,
): PropertySearchStage {
  const focus = record(c.imageFocus);
  const position =
    oneOf(["left", "center", "right"] as const, focus.x) &&
    oneOf(SEARCH_POSITIONS, focus.y)
      ? `${focus.x} ${focus.y}`
      : undefined;
  const tint = oneOf(["none", "dark", "light"] as const, c.tint) ?? "dark";
  const video =
    c.mediaKind === "video"
      ? resolveBackground({
          kind: "video",
          video: {
            ...record(c.video),
            url: c.videoUrl,
            type: c.videoType ?? "realequity",
          },
          image: c.imageUrl,
          tint,
          position,
        })
      : undefined;
  const background =
    video && video.kind !== "none"
      ? video
      : resolveBackground({ kind: "image", image: c.imageUrl, tint, position });
  return {
    background,
    ink:
      background.kind === "none"
        ? undefined
        : tint === "light"
          ? "dark"
          : "light",
    align:
      oneOf(["left", "center", "right"] as const, c.styles?.content?.align) ??
      "left",
    position: oneOf(SEARCH_POSITIONS, c.contentPosition) ?? "center",
  };
}
